/* eslint-disable react/jsx-no-bind */
import React from 'react';
import styled from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import { Box, Flex, Image, Link, Text, theme } from 'pcln-design-system';
import { ArrowRight } from 'pcln-icons';
import { Carousel } from 'pcln-carousel';
import HeroItem from './HeroItem';
const Wrapper = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "HeroCollection__Wrapper",
  componentId: "sc-1ueexg0-0"
})(["max-width:1232px;gap:16px;& div.carousel__slide{width:auto !important;}"]);
const StyledImage = /*#__PURE__*/styled(Image).withConfig({
  displayName: "HeroCollection__StyledImage",
  componentId: "sc-1ueexg0-1"
})(["object-fit:cover;"]);
const GradientWrapper = /*#__PURE__*/styled(Box).withConfig({
  displayName: "HeroCollection__GradientWrapper",
  componentId: "sc-1ueexg0-2"
})(["position:relative;&:hover:after{background:", ";}&:after{content:'';position:absolute;border-radius:12px;inset:0;background:linear-gradient( 180deg,rgba(0,0,0,0) 45.97%,rgba(0,0,0,0.4) 62.18%,rgba(0,0,0,0.6) 78.39% );}"], ({
  isLink
}) => isLink ? 'linear-gradient( 180deg,rgba(0, 0, 0, 0) 45.97%, rgba(0, 0, 0, 0.5) 62.18%, rgba(0, 0, 0, 0.7) 78.39%)' : 'linear-gradient(180deg, rgba(0, 0, 0, 0) 45.97%, rgba(0, 0, 0, 0.4) 62.18%, rgba(0, 0, 0, 0.6) 78.39%)');
const HeroImageTextWrapper = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "HeroCollection__HeroImageTextWrapper",
  componentId: "sc-1ueexg0-3"
})(["position:absolute;bottom:20%;z-index:1;gap:4px;"]);
function HeroImageWrapper({
  children,
  heroLink,
  onClick
}) {
  if (heroLink) {
    return React.createElement(Link, {
      href: heroLink.url,
      target: heroLink.openInNewTab ? '_blank' : '_self',
      onClick: onClick
    }, children);
  }
  return React.createElement(Box, null, children);
}
/** @public */
export default function HeroCollection({
  heroCollection,
  handleSelect,
  overflowAllowancePxX = 0
}) {
  const {
    collectionHeading,
    collectionLink,
    collectionSubheading,
    heroImage,
    heroHeading,
    heroBody,
    heroLink,
    collection
  } = heroCollection;
  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme.breakpoints.md})`
  });
  return React.createElement(Wrapper, {
    "data-testid": 'hero-collection',
    flexDirection: 'column'
  }, collectionHeading && React.createElement(Flex, {
    flexDirection: 'column',
    style: {
      gap: '4px'
    }
  }, React.createElement(Flex, {
    alignItems: 'center',
    style: {
      gap: '24px'
    }
  }, React.createElement(Text, {
    textStyle: 'heading3',
    color: 'text.heading'
  }, collectionHeading), collectionLink && isDesktop && React.createElement(Link, {
    href: collectionLink.url,
    target: collectionLink.openInNewTab ? '_blank' : '_self',
    onClick: () => {
      handleSelect(collectionLink.analyticsTracking);
    },
    "aria-label": collectionLink.ariaLabel
  }, React.createElement(Flex, {
    alignItems: 'center',
    style: {
      gap: '8px'
    },
    pt: 1
  }, React.createElement(Text, {
    bold: true,
    fontSize: 1,
    color: 'primary',
    "data-testid": 'top-collection-link'
  }, collectionLink.label), React.createElement(ArrowRight, null)))), collectionSubheading && React.createElement(Text, {
    textStyle: 'subheading5',
    color: 'text.heading'
  }, collectionSubheading)), heroImage && React.createElement(HeroImageWrapper, {
    heroLink: heroLink,
    onClick: () => {
      handleSelect(heroLink.analyticsTracking);
    }
  }, React.createElement(GradientWrapper, {
    isLink: heroLink !== undefined,
    borderRadius: 'lg'
  }, React.createElement(StyledImage, {
    alt: heroImage.altText,
    loading: 'lazy',
    src: `${heroImage.asset?.file?.url}?fm=webp&w=1232&q=90`,
    width: '1232px',
    height: '364px',
    borderRadius: 'lg'
  }), React.createElement(HeroImageTextWrapper, {
    width: 'fit-content',
    flexDirection: 'column',
    px: 3
  }, heroHeading && React.createElement(Text, {
    textStyle: 'heading3',
    color: 'text.lightest'
  }, heroHeading), React.createElement(Flex, {
    flexDirection: ['column', null, 'row'],
    alignItems: ['flex-start', null, 'center'],
    style: {
      columnGap: '24px',
      rowGap: '8px'
    }
  }, heroBody && React.createElement(Text, {
    textStyle: 'subheading5',
    color: 'text.lightest'
  }, heroBody), heroLink && React.createElement(Link, {
    href: heroLink.url,
    target: heroLink.openInNewTab ? '_blank' : '_self',
    "aria-label": heroLink.ariaLabel,
    onClick: () => {
      handleSelect(heroLink.analyticsTracking);
    }
  }, React.createElement(Flex, {
    alignItems: 'center'
  }, React.createElement(Text, {
    textStyle: 'paragraphBold',
    color: 'text.lightest'
  }, heroLink.label), React.createElement(ArrowRight, {
    ml: 2,
    color: 'text.lightest'
  }))))))), React.createElement(Box, {
    mt: heroImage ? ['-12%', null, '-8%', null, '-7%', '-5%'] : 0
  }, React.createElement(Carousel, {
    visibleSlides: [2.5, 3.5, 6],
    mobileVisibleSlides: [2.2, 2.5],
    overflowAllowancePxX: overflowAllowancePxX
  }, collection.map((item, index) => React.createElement(HeroItem, {
    key: `${item.heading}-${index}`,
    imageSrc: item.image?.asset?.file?.url,
    alt: item.image?.altText,
    heading: item.heading,
    paragraph: item.paragraph,
    href: item.link?.url,
    onClick: () => {
      handleSelect(item.link?.analyticsTracking);
    }
  })))), collectionLink && !isDesktop && React.createElement(Link, {
    "data-testid": 'bottom-collection-link',
    variation: 'subtle',
    href: collectionLink.url,
    target: collectionLink.openInNewTab ? '_blank' : '_self',
    onClick: () => {
      handleSelect(collectionLink.analyticsTracking);
    },
    "aria-label": collectionLink.ariaLabel
  }, React.createElement(Text, {
    bold: true,
    fontSize: 1,
    color: 'primary'
  }, collectionLink.label)));
}